import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { api } from '../lib/api';
import { PAYMENT_METHODS } from '../lib/constants';
import { formatCurrency, formatDate } from '@/lib/format';
import { toast } from 'sonner';
import { AlertTriangle, Search, X, Loader2, Ban, CheckCircle2 } from 'lucide-react';

export default function PaymentCancellationModal({
    open,
    onOpenChange,
    onSuccess,
    collections = []
}) {
    const [customers, setCustomers] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [customerSearch, setCustomerSearch] = useState('');
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const [payments, setPayments] = useState([]);
    const [loadingPayments, setLoadingPayments] = useState(false);
    const [selectedPaymentId, setSelectedPaymentId] = useState('');
    const [cancelReason, setCancelReason] = useState('');
    const [confirmed, setConfirmed] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    // Reset when modal opens / closes
    useEffect(() => {
        if (open) {
            fetchCustomers();
            setSelectedCustomerId('');
            setSelectedCustomer(null);
            setPayments([]);
            setSelectedPaymentId('');
            setCancelReason('');
            setConfirmed(false);
            setCustomerSearch('');
        }
    }, [open]);

    // Fetch customers
    const fetchCustomers = async () => {
        setLoadingCustomers(true);
        try {
            const res = await api.get('/customers?page_size=200&is_active=true');
            const items = res.data?.items || (Array.isArray(res.data) ? res.data : []);
            setCustomers(items);
        } catch (err) {
            console.error("Müşteriler yüklenemedi", err);
        } finally {
            setLoadingCustomers(false);
        }
    };

    // Merge API customers with distinct customers found in ledger collections
    const allCustomers = useMemo(() => {
        const map = new Map();
        customers.forEach(c => {
            if (c.id) map.set(c.id, c);
        });
        collections.forEach(c => {
            const custId = c.customer_id || c.customer_name;
            if (custId && !map.has(custId)) {
                map.set(custId, {
                    id: custId,
                    display_name: c.customer_name || 'Bilinmiyor',
                    customer_number: c.customer_number || '',
                });
            }
        });
        return Array.from(map.values());
    }, [customers, collections]);

    // Filter customers based on search text
    const filteredCustomers = useMemo(() => {
        const q = customerSearch.toLocaleLowerCase('tr-TR').trim();
        if (!q) return allCustomers;
        return allCustomers.filter(c =>
            (c.display_name && c.display_name.toLocaleLowerCase('tr-TR').includes(q)) ||
            (c.company_name && c.company_name.toLocaleLowerCase('tr-TR').includes(q)) ||
            (c.customer_number && c.customer_number.toLocaleLowerCase('tr-TR').includes(q))
        );
    }, [allCustomers, customerSearch]);

    // Handle customer selection
    const handleCustomerChange = async (custId) => {
        setSelectedCustomerId(custId);
        setSelectedPaymentId('');
        const found = allCustomers.find(c => c.id === custId);
        setSelectedCustomer(found || null);

        if (!custId) {
            setPayments([]);
            return;
        }

        setLoadingPayments(true);
        try {
            const res = await api.get(`/customer-payments?customer_id=${custId}`);
            const data = Array.isArray(res.data) ? res.data : (res.data?.items || []);
            // Only show payments that are eligible for cancellation (not cancelled)
            const activePayments = data.filter(p => p.status !== 'cancelled');
            if (activePayments.length > 0) {
                setPayments(activePayments);
                if (activePayments.length === 1) {
                    setSelectedPaymentId(activePayments[0].id);
                }
            } else {
                throw new Error("No active payments in endpoint");
            }
        } catch (err) {
            // Fallback to collections ledger
            const fallback = collections.filter(
                c => (c.customer_id === custId || c.customer_name === custId) &&
                     c.source === 'customer_payment' &&
                     c.status !== 'cancelled'
            ).map(c => ({
                id: c.source_id,
                total_amount: c.amount,
                payment_date: c.collection_date,
                payment_method: c.payment_method,
                status: c.status,
                allocations: c.job_number ? [{ job_number: c.job_number, amount: c.amount }] : [],
                active_receipt_number: c.active_receipt_number || null,
            }));
            setPayments(fallback);
            if (fallback.length === 1) {
                setSelectedPaymentId(fallback[0].id);
            }
        } finally {
            setLoadingPayments(false);
        }
    };

    const selectedPayment = useMemo(() => {
        return payments.find(p => p.id === selectedPaymentId) || null;
    }, [payments, selectedPaymentId]);

    const associatedJobsText = useMemo(() => {
        if (!selectedPayment) return '';
        if (selectedPayment.allocations && selectedPayment.allocations.length > 0) {
            const jobNums = selectedPayment.allocations
                .map(a => a.job_number || (a.job_id ? `İş #${a.job_id}` : null))
                .filter(Boolean);
            if (jobNums.length > 0) {
                return jobNums.join(', ');
            }
        }
        return selectedPayment.job_number || 'Müşteri avansı (İş tahsisi yok)';
    }, [selectedPayment]);

    const handleConfirmCancel = async (e) => {
        e.preventDefault();
        const reason = cancelReason.trim();
        if (!selectedPaymentId || !reason || cancelling || !confirmed) return;

        setCancelling(true);
        try {
            await api.post(`/customer-payments/${selectedPaymentId}/cancel`, { reason });
            toast.success("Tahsilat iptal edildi.");
            if (onSuccess) await onSuccess();
            onOpenChange(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Tahsilat iptal edilemedi.");
        } finally {
            setCancelling(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={openState => { if (!openState && !cancelling) onOpenChange(false); }}>
            <DialogContent className="max-w-lg w-[95vw] p-6 bg-white max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-gp-graphite">
                        <Ban className="w-5 h-5 text-rose-600" />
                        Tahsilat İptali
                    </DialogTitle>
                    <DialogDescription>
                        Müşteriye ait aktif bir tahsilatı seçip iptal nedenini girerek iptal işlemini tamamlayabilirsiniz.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleConfirmCancel} className="space-y-4 pt-2">
                    {/* 1. Searchable Customer Selection */}
                    <div className="space-y-1.5">
                        <Label className="text-xs font-semibold text-slate-700">Müşteri Seçimi *</Label>
                        {!selectedCustomer ? (
                            <div className="space-y-2">
                                <div className="relative">
                                    <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                                    <Input
                                        placeholder="Müşteri ara (isim, firma, no)..."
                                        value={customerSearch}
                                        onChange={e => setCustomerSearch(e.target.value)}
                                        className="pl-8 text-xs h-9"
                                        data-testid="cancellation-customer-search-input"
                                    />
                                    {customerSearch && (
                                        <button
                                            type="button"
                                            onClick={() => setCustomerSearch('')}
                                            className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                                <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-44 overflow-y-auto bg-white" data-testid="cancellation-customer-list">
                                    {loadingCustomers ? (
                                        <div className="p-3 text-xs text-center text-slate-500">Müşteriler yükleniyor...</div>
                                    ) : filteredCustomers.length === 0 ? (
                                        <div className="p-3 text-xs text-center text-slate-500">Müşteri bulunamadı.</div>
                                    ) : (
                                        filteredCustomers.map(c => (
                                            <div
                                                key={c.id}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => handleCustomerChange(c.id)}
                                                onKeyDown={e => { if (e.key === 'Enter') handleCustomerChange(c.id); }}
                                                className="p-2.5 hover:bg-slate-50 cursor-pointer text-xs flex items-center justify-between transition-colors"
                                                data-testid={`customer-option-${c.id}`}
                                            >
                                                <div>
                                                    <span className="font-medium text-slate-800">{c.display_name}</span>
                                                    {c.company_name && (
                                                        <span className="text-slate-500 ml-1.5">({c.company_name})</span>
                                                    )}
                                                </div>
                                                {c.customer_number && (
                                                    <span className="font-mono text-[11px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                                                        {c.customer_number}
                                                    </span>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200 rounded-md">
                                <div>
                                    <span className="font-semibold text-xs text-slate-800">{selectedCustomer.display_name}</span>
                                    {selectedCustomer.customer_number && (
                                        <span className="ml-2 font-mono text-[11px] text-slate-500 bg-slate-200/70 px-1.5 py-0.5 rounded">
                                            {selectedCustomer.customer_number}
                                        </span>
                                    )}
                                    {selectedCustomer.company_name && (
                                        <span className="block text-[11px] text-slate-500">{selectedCustomer.company_name}</span>
                                    )}
                                </div>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        setSelectedCustomerId('');
                                        setSelectedCustomer(null);
                                        setSelectedPaymentId('');
                                        setPayments([]);
                                        setConfirmed(false);
                                    }}
                                    className="text-xs h-7 px-2 text-slate-600 hover:text-slate-900"
                                    data-testid="cancellation-change-customer-btn"
                                >
                                    Değiştir
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* 2. Active Payment Selection for the Customer */}
                    {selectedCustomerId && (
                        <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">İptal Edilecek Tahsilat *</Label>
                            {loadingPayments ? (
                                <div className="flex items-center gap-2 p-3 text-xs text-slate-500 bg-slate-50 rounded-md">
                                    <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                                    <span>Tahsilat kayıtları yükleniyor...</span>
                                </div>
                            ) : payments.length === 0 ? (
                                <div className="p-3 text-xs bg-amber-50 text-amber-800 border border-amber-200 rounded-md">
                                    Bu müşteriye ait iptal edilebilir aktif tahsilat kaydı bulunamadı.
                                </div>
                            ) : (
                                <div className="space-y-2 max-h-48 overflow-y-auto pr-0.5" data-testid="cancellation-payments-container">
                                    {payments.map(p => {
                                        const isSelected = p.id === selectedPaymentId;
                                        return (
                                            <div
                                                key={p.id}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => setSelectedPaymentId(p.id)}
                                                onKeyDown={e => { if (e.key === 'Enter') setSelectedPaymentId(p.id); }}
                                                className={`p-3 rounded-md border text-left cursor-pointer transition-all ${
                                                    isSelected
                                                        ? 'border-rose-500 bg-rose-50/40 ring-1 ring-rose-500'
                                                        : 'border-slate-200 hover:border-slate-300 bg-white'
                                                }`}
                                                data-testid={`payment-option-${p.id}`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <span className="font-semibold text-xs text-slate-900">
                                                        {formatCurrency(p.total_amount)}
                                                    </span>
                                                    <span className="text-[11px] text-slate-500">
                                                        {formatDate(p.payment_date)}
                                                    </span>
                                                </div>
                                                <div className="flex items-center justify-between mt-1 text-[11px] text-slate-600">
                                                    <span>{PAYMENT_METHODS[p.payment_method] || p.payment_method}</span>
                                                    <span className="text-slate-500 text-right">
                                                        {p.allocations?.map(a => a.job_number).filter(Boolean).join(', ') || p.job_number || 'Müşteri avansı'}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* 3. Payment Details Card */}
                    {selectedPayment && (
                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-2" data-testid="selected-payment-details">
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <span className="text-slate-500 block">Tahsilat Tarihi:</span>
                                    <span className="font-semibold text-slate-800">{formatDate(selectedPayment.payment_date)}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500 block">Tutar:</span>
                                    <span className="font-bold text-slate-900">{formatCurrency(selectedPayment.total_amount)}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500 block">Ödeme Yöntemi:</span>
                                    <span className="font-medium text-slate-800">{PAYMENT_METHODS[selectedPayment.payment_method] || selectedPayment.payment_method}</span>
                                </div>
                                <div>
                                    <span className="text-slate-500 block">İlişkili İş(ler):</span>
                                    <span className="font-medium text-slate-800">{associatedJobsText}</span>
                                </div>
                            </div>
                            {selectedPayment.active_receipt_number && (
                                <div className="mt-2 bg-amber-50 border border-amber-200 text-amber-900 p-2 rounded text-[11px] flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                                    <span>
                                        Bu tahsilata ait aktif Makbuz No: <strong>{selectedPayment.active_receipt_number}</strong> mevcuttur. Tahsilat iptal edildiğinde makbuz otomatik olarak hükümsüz (voided) hale getirilecektir.
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* 4. Mandatory Cancellation Reason */}
                    {selectedPayment && (
                        <div className="space-y-1.5">
                            <Label htmlFor="cancel-payment-reason" className="text-xs font-semibold text-slate-700">
                                İptal Nedeni *
                            </Label>
                            <Textarea
                                id="cancel-payment-reason"
                                required
                                maxLength={500}
                                rows={3}
                                value={cancelReason}
                                onChange={e => setCancelReason(e.target.value)}
                                placeholder="İptal nedenini açıklayınız (zorunlu)..."
                                className="text-xs border-slate-300"
                                data-testid="cancel-payment-reason-input"
                            />
                        </div>
                    )}

                    {/* 5. Explicit Confirmation Before Cancellation */}
                    {selectedPayment && (
                        <div className="flex items-start space-x-2 p-3 bg-rose-50/60 border border-rose-200 rounded-md">
                            <Checkbox
                                id="cancel-payment-confirm"
                                checked={confirmed}
                                onCheckedChange={setConfirmed}
                                data-testid="cancel-payment-confirm-checkbox"
                            />
                            <Label
                                htmlFor="cancel-payment-confirm"
                                className="text-xs text-rose-900 font-medium leading-snug cursor-pointer select-none"
                            >
                                Bu tahsilat kaydının ve varsa bağlı tahsilat makbuzunun iptal edileceğini, bu işlemin geri alınamayacağını onaylıyorum.
                            </Label>
                        </div>
                    )}

                    <DialogFooter className="flex items-center justify-end gap-2 pt-2 border-t">
                        <Button
                            type="button"
                            variant="outline"
                            disabled={cancelling}
                            onClick={() => onOpenChange(false)}
                        >
                            Vazgeç
                        </Button>
                        <Button
                            type="submit"
                            variant="destructive"
                            disabled={cancelling || !selectedPaymentId || !cancelReason.trim() || !confirmed}
                            data-testid="confirm-cancel-payment-btn"
                        >
                            {cancelling ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                                    İptal Ediliyor...
                                </>
                            ) : (
                                'Tahsilatı İptal Et'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
